import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
  FormControlLabel, Paper, Switch, TextField, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { supabase } from '../../supabaseClient'

// The important-dates manager. Staff (office managers do most of this work; admins
// too) check each fair's website and enter the four canonical dates for the current
// competition season. Fairs are sorted by number of participating students so the
// ones that matter float to the top. `lockActiveOnly` hides fairs with zero students
// entirely (office-manager view) — they only work fairs students are actually on.

const FALLBACK_SEASON = 2027

const SEASON_DATE_FIELDS: Array<{
  key: 'pre_approval_date' | 'registration_date' | 'submission_date' | 'held_date'
  label: string
}> = [
  { key: 'pre_approval_date', label: 'Pre-Approval / SRC Review' },
  { key: 'registration_date', label: 'Regular Registration' },
  { key: 'submission_date',   label: 'Project Submission' },
  { key: 'held_date',         label: 'Fair Held' },
]

interface FairSeasonRow {
  pre_approval_date: string | null
  registration_date: string | null
  submission_date:   string | null
  held_date:         string | null
  dates_confirmed:   boolean
  dates_updated_at:  string | null
  last_checked_at:   string | null
  needs_review:      boolean
  proposed_dates:    Record<string, string | null> | null
}

interface FairDates {
  id:     string
  name:   string
  county: string | null
  state:  string | null
  active: number             // # of active student projects on this fair
  fs:     FairSeasonRow | null
}

type DateEdit = Partial<Pick<FairSeasonRow,
  'pre_approval_date' | 'registration_date' | 'submission_date' | 'held_date' | 'dates_confirmed'>>

export default function FairDatesManager({ lockActiveOnly = false }: { lockActiveOnly?: boolean }) {
  const [season,     setSeason]     = useState(FALLBACK_SEASON)
  const [fairs,      setFairs]      = useState<FairDates[]>([])
  const [loading,    setLoading]    = useState(true)
  const [expanded,   setExpanded]   = useState<string | null>(null)
  const [saving,     setSaving]     = useState<string | null>(null)
  const [edits,      setEdits]      = useState<Record<string, DateEdit>>({})
  const [activeOnly, setActiveOnly] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const { data: setting } = await supabase.from('app_setting')
        .select('value').eq('key', 'current_competition_season').maybeSingle()
      const curSeason = Number((setting as { value: string } | null)?.value) || FALLBACK_SEASON

      const [{ data: sf }, { data: entries }, { data: active }] = await Promise.all([
        supabase.from('science_fair').select('id, name, county, state').order('name'),
        supabase.from('project_fair_entry').select('science_fair_id, project_id'),
        supabase.from('project').select('project_id').eq('status', 'active'),
      ])
      const { data: seasons } = await supabase.from('fair_season')
        .select('science_fair_id, pre_approval_date, registration_date, submission_date, held_date, dates_confirmed, dates_updated_at, last_checked_at, needs_review, proposed_dates')
        .eq('season', curSeason)
      if (cancelled) return

      const activeIds = new Set((active ?? []).map((p: { project_id: string }) => p.project_id))
      const countByFair: Record<string, number> = {}
      for (const e of (entries ?? []) as { science_fair_id: string; project_id: string }[]) {
        if (activeIds.has(e.project_id)) countByFair[e.science_fair_id] = (countByFair[e.science_fair_id] ?? 0) + 1
      }
      const fsByFair: Record<string, FairSeasonRow> = {}
      for (const r of (seasons ?? []) as (FairSeasonRow & { science_fair_id: string })[]) {
        const { science_fair_id, ...rest } = r
        fsByFair[science_fair_id] = rest
      }

      const rows: FairDates[] = (sf ?? []).map((f: { id: string; name: string; county: string | null; state: string | null }) => ({
        id: f.id, name: f.name, county: f.county, state: f.state,
        active: countByFair[f.id] ?? 0,
        fs: fsByFair[f.id] ?? null,
      }))
      // Most-participated fairs first — that's the office manager's work queue.
      rows.sort((a, b) => (b.active - a.active) || a.name.localeCompare(b.name))
      setSeason(curSeason)
      setFairs(rows)
      setLoading(false)
    })()
    return () => { cancelled = true }
  }, [])

  function edit(id: string, field: keyof DateEdit, value: string | boolean) {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
  }
  function discard(id: string) {
    setEdits(prev => { const next = { ...prev }; delete next[id]; return next })
  }
  function applyProposed(fair: FairDates) {
    const p = fair.fs?.proposed_dates ?? {}
    setEdits(prev => ({ ...prev, [fair.id]: {
      pre_approval_date: p.pre_approval_date ?? null,
      registration_date: p.registration_date ?? null,
      submission_date:   p.submission_date ?? null,
      held_date:         p.held_date ?? null,
    } }))
    setExpanded(fair.id)
  }

  async function save(fair: FairDates) {
    setSaving(fair.id)
    const patch = edits[fair.id] ?? {}
    const fs = fair.fs
    const merged: FairSeasonRow = {
      pre_approval_date: fs?.pre_approval_date ?? null,
      registration_date: fs?.registration_date ?? null,
      submission_date:   fs?.submission_date ?? null,
      held_date:         fs?.held_date ?? null,
      dates_confirmed:   fs?.dates_confirmed ?? false,
      dates_updated_at:  fs?.dates_updated_at ?? null,
      last_checked_at:   fs?.last_checked_at ?? null,
      needs_review:      false,
      proposed_dates:    null,
      ...patch,
    }
    // Only bump dates_updated_at when a date actually changed (not a lone confirm
    // toggle) — that flag drives the student "Dates updated" chip.
    const dateChanged = SEASON_DATE_FIELDS.some(({ key }) =>
      patch[key] !== undefined && (patch[key] || null) !== (fs?.[key] ?? null))

    const row: Record<string, unknown> = {
      science_fair_id:   fair.id,
      season,
      pre_approval_date: merged.pre_approval_date || null,
      registration_date: merged.registration_date || null,
      submission_date:   merged.submission_date || null,
      held_date:         merged.held_date || null,
      dates_confirmed:   merged.dates_confirmed,
      needs_review:      false,
      proposed_dates:    null,
    }
    if (dateChanged) row.dates_updated_at = new Date().toISOString()

    const { error } = await supabase.from('fair_season').upsert(row, { onConflict: 'science_fair_id,season' })
    if (!error) {
      setFairs(prev => prev.map(f => f.id === fair.id ? { ...f, fs: {
        ...merged,
        dates_updated_at: dateChanged ? (row.dates_updated_at as string) : merged.dates_updated_at,
      } } : f))
      discard(fair.id)
    }
    setSaving(null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  // Office-manager view: never show zero-student fairs. Admins can toggle.
  const effectiveActiveOnly = lockActiveOnly || activeOnly
  const shown = effectiveActiveOnly ? fairs.filter(f => f.active > 0) : fairs
  const fmtDate = (d: string | null) => d
    ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'TBD'

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Competition season <strong>{season}</strong>. {shown.length} fair{shown.length !== 1 ? 's' : ''}, most students first.
          A date left blank shows as <strong>TBD</strong> to students.
        </Typography>
        {!lockActiveOnly && (
          <FormControlLabel
            control={<Switch size="small" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />}
            label={<Typography variant="body2">Only fairs with active projects</Typography>}
          />
        )}
      </Box>

      {shown.length === 0 && (
        <Alert severity="info">No fairs with students on them yet.</Alert>
      )}

      {shown.map(fair => {
        const patch     = edits[fair.id] ?? {}
        const fs        = fair.fs
        const cur       = { ...fs, ...patch }
        const isDirty   = Object.keys(patch).length > 0
        const isOpen    = expanded === fair.id
        const confirmed = cur.dates_confirmed ?? false

        return (
          <Paper key={fair.id} variant="outlined" sx={{ mb: 2 }}>
            <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
              onClick={() => setExpanded(isOpen ? null : fair.id)}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{fair.name}</Typography>
                  {fair.active > 0 && <Chip label={`${fair.active} student${fair.active !== 1 ? 's' : ''}`} size="small" color="primary" />}
                  {fair.fs?.needs_review && <Chip label="Needs review" size="small" color="warning" />}
                  {confirmed
                    ? <Chip label="Dates confirmed" size="small" color="success" />
                    : <Chip label="Not confirmed" size="small" />}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {[fair.county, fair.state].filter(Boolean).join(', ')}
                  {' · '}
                  {SEASON_DATE_FIELDS.map(({ key }) => fmtDate(cur[key] ?? null)).join(' / ')}
                  {fair.fs?.last_checked_at ? ` · Checked ${new Date(fair.fs.last_checked_at).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>

            <Collapse in={isOpen}>
              <Divider />
              <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {fair.fs?.needs_review && fair.fs.proposed_dates && (
                  <Alert severity="warning" action={
                    <Button color="inherit" size="small" onClick={() => applyProposed(fair)}>Apply proposed</Button>
                  }>
                    The checker found possibly-updated dates:{' '}
                    {SEASON_DATE_FIELDS.map(({ key, label }) =>
                      `${label}: ${fmtDate(fair.fs?.proposed_dates?.[key] ?? null)}`).join(' · ')}
                    . Review and apply, then save to publish.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {SEASON_DATE_FIELDS.map(({ key, label }) => (
                    <TextField
                      key={key} type="date" label={label} size="small" sx={{ flex: '1 1 200px' }}
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={cur[key] ?? ''}
                      onChange={e => edit(fair.id, key, e.target.value)}
                    />
                  ))}
                </Box>

                <FormControlLabel
                  control={<Switch checked={confirmed} onChange={e => edit(fair.id, 'dates_confirmed', e.target.checked)} />}
                  label={<Typography variant="body2">Dates confirmed for {season}</Typography>}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {isDirty && <Button variant="outlined" size="small" onClick={() => discard(fair.id)}>Discard</Button>}
                  <Button variant="contained" size="small" disabled={!isDirty || saving === fair.id} onClick={() => save(fair)}>
                    {saving === fair.id ? <CircularProgress size={16} color="inherit" /> : 'Save Dates'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Paper>
        )
      })}
    </Box>
  )
}
